import { useEffect, useState } from "react";
import {
  Box,
  Button,
  Flex,
  Heading,
  Input,
  SimpleGrid,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  useToast,
} from "@chakra-ui/react";
import { DateTime } from "luxon";
import { FaPercent, FaSearch, FaSyncAlt } from "react-icons/fa";
import { crm, Pill } from "../../../theme";
import { IcmsStateRate, NcmTaxRate } from "../../../models/Tax";
import {
  getContributionOverrides,
  getIcmsRates,
  getNcmTaxRate,
  getTaxStatus,
  importTaxRates,
  setContributionOverride,
  updateIcmsRate,
} from "../../../services/taxService";
import { formatNcm, TableCard, tdProps, thProps } from "../../process/components/tableStyles";
import { toneColors } from "../../process/components/duimp";

const pct = (value?: number | null) =>
  value == null ? "—" : `${value.toLocaleString("pt-BR", { maximumFractionDigits: 4 })}%`;

const toNumber = (value: string) => {
  const parsed = Number(value.replace(",", "."));
  return value.trim() === "" || Number.isNaN(parsed) ? null : parsed;
};

// Rascunho em texto: digitar "19,5" não pode virar número a cada tecla
type IcmsDraft = { uf: string; rate: string; effectiveRate: string; notes: string };

const toDraft = (r: IcmsStateRate): IcmsDraft => ({
  uf: r.uf,
  rate: r.rate?.toString().replace(".", ",") ?? "",
  effectiveRate: r.effectiveRate?.toString().replace(".", ",") ?? "",
  notes: r.notes ?? "",
});

const Section = ({ title, subtitle, children }: { title: string; subtitle?: string; children: React.ReactNode }) => (
  <Box bg="white" borderRadius="12px" border={`1px solid ${crm.border}`} boxShadow={crm.shadowSm} p="24px" mb="24px">
    <Text fontSize="16px" fontWeight="700" color={crm.text}>
      {title}
    </Text>
    {subtitle && (
      <Text fontSize="13px" color={crm.textMuted} mt="2px" mb="16px">
        {subtitle}
      </Text>
    )}
    {children}
  </Box>
);

const RateStat = ({ label, value, hint }: { label: string; value: string; hint?: string }) => (
  <Box p="12px 14px" borderRadius={crm.radiusSm} border={`1px solid ${crm.border}`} bg={crm.subtle}>
    <Text fontSize="12px" fontWeight="600" color={crm.textMuted}>
      {label}
    </Text>
    <Text fontSize="18px" fontWeight="700" color={crm.text}>
      {value}
    </Text>
    {hint && (
      <Text fontSize="11px" color={crm.textFaint}>
        {hint}
      </Text>
    )}
  </Box>
);

/** Configuração do motor de tributos (admin): TEC/TIPI, exceções de PIS/COFINS e ICMS por UF. */
const TaxSettings = () => {
  const toast = useToast();
  const [lastImport, setLastImport] = useState<string>();
  const [importing, setImporting] = useState(false);
  const [ncmQuery, setNcmQuery] = useState("");
  const [ncm, setNcm] = useState<NcmTaxRate | null>();
  const [pisDraft, setPisDraft] = useState("");
  const [cofinsDraft, setCofinsDraft] = useState("");
  const [overrides, setOverrides] = useState<NcmTaxRate[]>([]);
  const [icms, setIcms] = useState<IcmsDraft[]>([]);
  const [savingUf, setSavingUf] = useState<string>();

  const load = async () => {
    const [status, contributionList, icmsList] = await Promise.all([
      getTaxStatus(),
      getContributionOverrides(),
      getIcmsRates(),
    ]);
    setLastImport(status.lastImport);
    setOverrides(contributionList);
    setIcms(icmsList.map(toDraft));
  };

  useEffect(() => {
    load().catch(() => toast({ title: "Não foi possível carregar os tributos", status: "error" }));
  }, []);

  const runImport = async () => {
    setImporting(true);
    try {
      const result = await importTaxRates();
      toast({
        title: result.success ? "Tabelas TEC/TIPI atualizadas" : "Falha ao atualizar TEC/TIPI",
        description: result.success
          ? `${result.tecNcms} NCMs na TEC, ${result.tipiNcms} na TIPI (${result.tecFile})`
          : result.message,
        status: result.success ? "success" : "error",
        duration: 8000,
      });
      await load();
    } catch {
      toast({ title: "Falha ao atualizar TEC/TIPI", status: "error" });
    } finally {
      setImporting(false);
    }
  };

  const searchNcm = async () => {
    const digits = ncmQuery.replace(/\D/g, "");
    if (digits.length !== 8) {
      toast({ title: "Informe o NCM com 8 dígitos", status: "warning" });
      return;
    }
    const result = await getNcmTaxRate(digits);
    setNcm(result ?? null);
    setPisDraft(result?.pisRate?.toString() ?? "");
    setCofinsDraft(result?.cofinsRate?.toString() ?? "");
  };

  const saveOverride = async (target: string, pis: number | null, cofins: number | null) => {
    try {
      const saved = await setContributionOverride(target, pis, cofins);
      if (ncm?.ncm === saved.ncm) setNcm(saved);
      setOverrides(await getContributionOverrides());
      toast({ title: "Exceção de PIS/COFINS salva", status: "success" });
    } catch {
      toast({ title: "Alíquota inválida", status: "error" });
    }
  };

  const editIcms = (uf: string, patch: Partial<IcmsDraft>) =>
    setIcms((list) => list.map((r) => (r.uf === uf ? { ...r, ...patch } : r)));

  const saveIcms = async (draft: IcmsDraft) => {
    const rate = toNumber(draft.rate);
    if (rate == null) {
      toast({ title: `Informe a alíquota de ${draft.uf}`, status: "warning" });
      return;
    }
    setSavingUf(draft.uf);
    try {
      const saved = await updateIcmsRate({
        uf: draft.uf,
        rate,
        effectiveRate: toNumber(draft.effectiveRate),
        notes: draft.notes.trim() || null,
      });
      editIcms(draft.uf, toDraft(saved));
      toast({ title: `ICMS ${draft.uf} salvo`, status: "success", duration: 2000 });
    } catch {
      toast({ title: `Alíquota de ${draft.uf} inválida`, status: "error" });
    } finally {
      setSavingUf(undefined);
    }
  };

  const importedAt = lastImport ? DateTime.fromISO(lastImport) : undefined;

  return (
    <Box>
      <Heading size="lg" mb="24px" fontWeight="normal">
        <Flex alignItems="center" gap="12px">
          <FaPercent color="var(--icon-color)" /> Tributos de importação
        </Flex>
      </Heading>

      <Section
        title="Tabelas oficiais (TEC e TIPI)"
        subtitle="II vem da TEC (Camex) e IPI da TIPI (Receita Federal). O robô baixa as planilhas toda segunda-feira."
      >
        <Flex align="center" gap="16px" wrap="wrap">
          <Pill {...toneColors[importedAt?.isValid ? "success" : "warning"]}>
            {importedAt?.isValid
              ? `Atualizado em ${importedAt.toFormat("dd/LL/yyyy 'às' HH:mm")}`
              : "Ainda não importado"}
          </Pill>
          <Button
            size="sm"
            leftIcon={<FaSyncAlt />}
            bg={crm.brand}
            color="white"
            _hover={{ bg: crm.brandDark }}
            isLoading={importing}
            loadingText="Baixando planilhas"
            onClick={runImport}
          >
            Atualizar agora
          </Button>
        </Flex>
      </Section>

      <Section
        title="Consultar NCM"
        subtitle="Alíquotas usadas no simulador. PIS/COFINS seguem o padrão salvo exceção cadastrada aqui."
      >
        <Flex gap="8px" mb="16px" maxW="420px">
          <Input
            placeholder="NCM (ex.: 8504.40.10)"
            value={ncmQuery}
            onChange={(e) => setNcmQuery(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && searchNcm()}
          />
          <Button leftIcon={<FaSearch />} onClick={searchNcm} variant="outline">
            Buscar
          </Button>
        </Flex>
        {ncm === null && <Text color={crm.textFaint}>NCM não encontrado nas tabelas importadas.</Text>}
        {ncm && (
          <Box>
            <Text fontWeight="600" mb="12px">
              {formatNcm(ncm.ncm)} · {ncm.description ?? "sem descrição"}
            </Text>
            <SimpleGrid columns={{ base: 1, sm: 2, lg: 4 }} spacing="12px" mb="12px">
              <RateStat
                label="II"
                value={pct(ncm.iiRate)}
                hint={`${ncm.iiSource ?? "sem fonte"}${ncm.iiHasExceptions ? " · tem Ex/quota" : ""}`}
              />
              <RateStat
                label="IPI"
                value={ncm.ipiNotTaxed ? "NT" : pct(ncm.ipiRate)}
                hint={`TIPI${ncm.ipiHasExceptions ? " · tem Ex" : ""}`}
              />
              <RateStat
                label="PIS-Importação"
                value={pct(ncm.pisRate ?? ncm.defaultPisRate)}
                hint={ncm.pisRate != null ? "Exceção cadastrada" : "Padrão"}
              />
              <RateStat
                label="COFINS-Importação"
                value={pct(ncm.cofinsRate ?? ncm.defaultCofinsRate)}
                hint={ncm.cofinsRate != null ? "Exceção cadastrada" : "Padrão"}
              />
            </SimpleGrid>
            {ncm.observedDuimp && (
              <Text fontSize="13px" color={crm.textMuted} mb="12px">
                Aplicado na DUIMP {ncm.observedDuimp}: II {pct(ncm.observedIiRate)} · IPI {pct(ncm.observedIpiRate)} ·
                PIS {pct(ncm.observedPisRate)} · COFINS {pct(ncm.observedCofinsRate)}
              </Text>
            )}
            <Flex gap="8px" align="flex-end" wrap="wrap">
              <Box>
                <Text fontSize="12px" color={crm.textMuted}>
                  Exceção PIS (%)
                </Text>
                <Input size="sm" w="120px" value={pisDraft} onChange={(e) => setPisDraft(e.target.value)} placeholder="padrão" />
              </Box>
              <Box>
                <Text fontSize="12px" color={crm.textMuted}>
                  Exceção COFINS (%)
                </Text>
                <Input size="sm" w="120px" value={cofinsDraft} onChange={(e) => setCofinsDraft(e.target.value)} placeholder="padrão" />
              </Box>
              <Button size="sm" onClick={() => saveOverride(ncm.ncm, toNumber(pisDraft), toNumber(cofinsDraft))}>
                Salvar exceção
              </Button>
            </Flex>
          </Box>
        )}
      </Section>

      <Section title="Exceções de PIS/COFINS" subtitle="NCMs com alíquota diferente do padrão (2,10% / 9,65%).">
        {overrides.length === 0 ? (
          <Text color={crm.textFaint}>Nenhuma exceção cadastrada.</Text>
        ) : (
          <TableCard>
            <Thead>
              <Tr>
                <Th {...thProps}>NCM</Th>
                <Th {...thProps}>Descrição</Th>
                <Th {...thProps} isNumeric>PIS</Th>
                <Th {...thProps} isNumeric>COFINS</Th>
                <Th {...thProps} />
              </Tr>
            </Thead>
            <Tbody>
              {overrides.map((o) => (
                <Tr key={o.ncm}>
                  <Td {...tdProps} fontWeight="600">{formatNcm(o.ncm)}</Td>
                  <Td {...tdProps} maxW="320px" whiteSpace="normal">{o.description ?? "—"}</Td>
                  <Td {...tdProps} isNumeric>{pct(o.pisRate ?? o.defaultPisRate)}</Td>
                  <Td {...tdProps} isNumeric>{pct(o.cofinsRate ?? o.defaultCofinsRate)}</Td>
                  <Td {...tdProps} textAlign="right">
                    <Button size="xs" variant="ghost" colorScheme="red" onClick={() => saveOverride(o.ncm, null, null)}>
                      Voltar ao padrão
                    </Button>
                  </Td>
                </Tr>
              ))}
            </Tbody>
          </TableCard>
        )}
      </Section>

      <Section
        title="ICMS na importação por UF"
        subtitle="Alíquota interna (base por dentro). Carga efetiva é opcional: aplicada só quando marcada na simulação (benefício fiscal)."
      >
        <TableCard>
          <Thead>
            <Tr>
              <Th {...thProps}>UF</Th>
              <Th {...thProps}>Alíquota (%)</Th>
              <Th {...thProps}>Carga efetiva (%)</Th>
              <Th {...thProps}>Observação</Th>
              <Th {...thProps} />
            </Tr>
          </Thead>
          <Tbody>
            {icms.map((r) => (
              <Tr key={r.uf}>
                <Td {...tdProps} fontWeight="700">{r.uf}</Td>
                <Td {...tdProps}>
                  <Input
                    size="sm"
                    w="90px"
                    value={r.rate}
                    onChange={(e) => editIcms(r.uf, { rate: e.target.value })}
                  />
                </Td>
                <Td {...tdProps}>
                  <Input
                    size="sm"
                    w="90px"
                    placeholder="—"
                    value={r.effectiveRate}
                    onChange={(e) => editIcms(r.uf, { effectiveRate: e.target.value })}
                  />
                </Td>
                <Td {...tdProps}>
                  <Input
                    size="sm"
                    minW="200px"
                    value={r.notes}
                    onChange={(e) => editIcms(r.uf, { notes: e.target.value })}
                  />
                </Td>
                <Td {...tdProps} textAlign="right">
                  <Button size="xs" isLoading={savingUf === r.uf} onClick={() => saveIcms(r)}>
                    Salvar
                  </Button>
                </Td>
              </Tr>
            ))}
          </Tbody>
        </TableCard>
      </Section>
    </Box>
  );
};

export default TaxSettings;
